import React from 'react';
import { motion } from 'framer-motion';
import { 
  Heart, 
  Sparkles, 
  Users, 
  TrendingUp, 
  Zap, 
  Mail, 
  Search, 
  Linkedin,
  Target,
  Award,
  Building,
  BarChart,
  Lightbulb,
  MessageSquare,
  CheckCircle
} from 'lucide-react';
import MouseFollowEffect from '../components/MouseFollowEffect';

export default function WeCareStaffing() {
  return (
    <div className="page-container pt-20 min-h-screen">
      <div className="content-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mb-4">
            <div className="relative">
              <Heart className="w-16 h-16 text-golden animate-pulse" />
              <Sparkles className="w-8 h-8 text-blue-400 absolute -top-2 -right-2 animate-pulse" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            WeCare <span className="text-golden">Staffing</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Automated Lead Generation System
          </p>
          <div className="mt-6 max-w-3xl mx-auto bg-black/30 p-6 rounded-xl border border-gray-800">
            <p className="text-gray-300">
              A comprehensive lead generation solution designed specifically for WeCare Staffing to connect with high-quality healthcare facilities and professionals at scale, driving business growth and recruitment success.
            </p>
          </div>
        </motion.div>

        {/* Introduction Section */}
        <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-8 mb-12 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-6 text-golden">The Automated Lead Generation System</h2>
          <p className="text-gray-300 mb-6">
            Our system leverages cutting-edge automation to deliver massive personalized outreach campaigns, sending up to <span className="text-golden font-semibold">2,000 highly targeted emails daily</span> to healthcare facilities and professionals who need WeCare Staffing's services.
          </p>
          <p className="text-gray-300 mb-6">
            What makes this approach different is the level of personalization. We don't just send emails to random contacts—we specifically reach out to healthcare facilities that are actively hiring, ensuring our outreach is relevant and timely. This results in significantly higher engagement rates compared to traditional mass email campaigns.
          </p>
          <p className="text-gray-300">
            The system works 24/7, continuously generating leads while you focus on your core business activities. It's like having an entire business development team working around the clock, but at a fraction of the cost.
          </p>
        </MouseFollowEffect>

        {/* Why We're Having This Meeting Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 text-center">Why We're Having This Meeting</h2>
          <div className="bg-gradient-to-r from-golden/20 to-black/30 rounded-2xl p-8 border border-golden/30 max-w-4xl mx-auto">
            <div className="mb-6">
              <img 
                src="/System 3/Icebreaker.png" 
                alt="Personalized Outreach" 
                className="w-full rounded-xl"
              />
            </div>
            <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
              <h3 className="text-xl font-bold mb-4 text-golden">Our Initial Outreach</h3>
              <p className="text-gray-300 italic text-lg mb-4">
                "Love how you're using AI to tackle burnout in nursing teams. Your mission to align nurses with roles that match their strengths is super cool—figured I'd reach out."
              </p>
              <p className="text-gray-300">
                This personalized message resonated because it demonstrated our understanding of your unique approach to healthcare staffing. Now, we're ready to help scale this innovative solution to reach more healthcare facilities and nurses who need it.
              </p>
            </div>
          </div>
        </div>

        {/* System Components */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-8 text-center">System Components</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <div className="flex items-start mb-4">
                <Linkedin className="w-10 h-10 text-golden mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">LinkedIn Sales Navigator</h3>
                  <p className="text-gray-300">
                    We use LinkedIn Sales Navigator to identify and target the perfect prospects for WeCare Staffing. This powerful tool allows us to filter by industry, facility type, job title, and more, ensuring we're reaching decision-makers who are most likely to need your healthcare staffing services.
                  </p>
                </div>
              </div>
            </MouseFollowEffect>

            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <div className="flex items-start mb-4">
                <Zap className="w-10 h-10 text-golden mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Make.com</h3>
                  <p className="text-gray-300">
                    Make.com serves as the automation backbone of our system, connecting all components and orchestrating the entire workflow. It handles everything from data processing to campaign management, ensuring seamless operation without manual intervention.
                  </p>
                </div>
              </div>
            </MouseFollowEffect>

            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <div className="flex items-start mb-4">
                <Mail className="w-10 h-10 text-golden mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Instantly.ai</h3>
                  <p className="text-gray-300">
                    Instantly.ai handles the email delivery with industry-leading deliverability rates. It manages email warming, sending schedules, and follow-ups automatically. The platform's advanced analytics also provide real-time insights into campaign performance.
                  </p>
                </div>
              </div>
            </MouseFollowEffect>

            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <div className="flex items-start mb-4">
                <Search className="w-10 h-10 text-golden mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Any Mail Finder</h3>
                  <p className="text-gray-300">
                    This specialized tool helps us discover verified email addresses for your target prospects with exceptional accuracy. It ensures we're reaching the right people through their preferred communication channel, significantly increasing response rates.
                  </p>
                </div>
              </div>
            </MouseFollowEffect>
          </div>
        </div>

        {/* System Structure */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 text-center">System Structure</h2>
          <div className="bg-black/20 border border-gray-800 rounded-2xl p-8 max-w-4xl mx-auto">
            <div className="mb-6">
              <img 
                src="/System 3/Screenshot 2025-02-27 at 16.49.22.png" 
                alt="Lead Generation System Structure" 
                className="w-full rounded-xl"
              />
            </div>
            <h3 className="text-xl font-bold mb-4 text-golden">How It All Works Together</h3>
            <ol className="space-y-4 text-gray-300">
              <li className="flex items-start">
                <span className="text-golden font-bold mr-2">1.</span>
                <span><strong>Prospect Identification:</strong> LinkedIn Sales Navigator identifies ideal prospects based on WeCare Staffing's target healthcare facility profile.</span>
              </li>
              <li className="flex items-start">
                <span className="text-golden font-bold mr-2">2.</span>
                <span><strong>Data Enrichment:</strong> Any Mail Finder discovers and verifies email addresses for these prospects.</span>
              </li>
              <li className="flex items-start">
                <span className="text-golden font-bold mr-2">3.</span>
                <span><strong>Automation Orchestration:</strong> Make.com connects all tools and manages the workflow between platforms.</span>
              </li>
              <li className="flex items-start">
                <span className="text-golden font-bold mr-2">4.</span>
                <span><strong>Personalized Outreach:</strong> Instantly.ai delivers highly personalized emails with automatic follow-ups.</span>
              </li>
              <li className="flex items-start">
                <span className="text-golden font-bold mr-2">5.</span>
                <span><strong>Response Management:</strong> The system tracks responses and alerts your team when prospects engage.</span>
              </li>
              <li className="flex items-start">
                <span className="text-golden font-bold mr-2">6.</span>
                <span><strong>Continuous Optimization:</strong> We analyze results and refine the approach for ever-improving performance.</span>
              </li>
            </ol>
          </div>
        </div>

        {/* Campaign Metrics */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 text-center">Proven Results</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <h3 className="text-xl font-bold mb-4 text-golden">Campaign Metrics</h3>
              <div className="mb-6">
                <img 
                  src="/System 3/Metrics.png" 
                  alt="Campaign Metrics" 
                  className="w-full rounded-xl"
                />
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Open Rate:</span>
                  <div className="flex items-center">
                    <span className="text-golden font-bold">78%</span>
                    <span className="text-green-500 ml-2 text-sm">(+46% vs. industry avg.)</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Reply Rate:</span>
                  <div className="flex items-center">
                    <span className="text-golden font-bold">15%</span>
                    <span className="text-green-500 ml-2 text-sm">(+10% vs. industry avg.)</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Meeting Conversion:</span>
                  <div className="flex items-center">
                    <span className="text-golden font-bold">4.2%</span>
                    <span className="text-green-500 ml-2 text-sm">(+2.7% vs. industry avg.)</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Cost Per Meeting:</span>
                  <div className="flex items-center">
                    <span className="text-golden font-bold">$38</span>
                    <span className="text-green-500 ml-2 text-sm">(vs. $250+ industry avg.)</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <h3 className="text-xl font-bold mb-4 text-golden">What This Means for WeCare Staffing</h3>
              <div className="space-y-4 text-gray-300">
                <p>
                  Based on these metrics and your current market position, WeCare Staffing could expect:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <Target className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>40-60 qualified meetings per month with healthcare facilities</span>
                  </li>
                  <li className="flex items-start">
                    <Target className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>4-10 new facility contracts per month</span>
                  </li>
                  <li className="flex items-start">
                    <Target className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Estimated ROI of 250-350% within 90 days</span>
                  </li>
                  <li className="flex items-start">
                    <Target className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Significant brand awareness boost in your target markets</span>
                  </li>
                </ul>
                <p className="mt-4">
                  These results would transform WeCare Staffing's growth trajectory, creating a predictable and scalable lead generation engine for your healthcare staffing business.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Healthcare Industry Specifics */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 text-center">Tailored for Healthcare Staffing</h2>
          <div className="bg-black/20 border border-gray-800 rounded-2xl p-8 max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4 text-golden">Industry-Specific Targeting</h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <Target className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span><strong>Target Facilities:</strong> Hospitals, Clinics, Long-term Care Facilities, Rehabilitation Centers</span>
                  </li>
                  <li className="flex items-start">
                    <Target className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span><strong>Decision Makers:</strong> HR Directors, Nursing Directors, Department Heads, Facility Administrators</span>
                  </li>
                  <li className="flex items-start">
                    <Target className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span><strong>Facility Size:</strong> 50+ beds with active staffing needs</span>
                  </li>
                  <li className="flex items-start">
                    <Target className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span><strong>Geographic Focus:</strong> Customized to WeCare Staffing's service areas</span>
                  </li>
                </ul>
                <div className="mt-6">
                  <img 
                    src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80" 
                    alt="Business Meeting" 
                    className="w-full rounded-xl"
                  />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-4 text-golden">Healthcare-Focused Messaging</h3>
                <p className="text-gray-300 mb-4">
                  Our messaging strategy addresses the specific pain points that drive healthcare facilities to seek staffing solutions:
                </p>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Staff shortages and burnout concerns</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Difficulty finding qualified healthcare professionals</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Seasonal staffing fluctuations</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Compliance and credentialing challenges</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Patient care quality maintenance</span>
                  </li>
                </ul>
                <div className="mt-6">
                  <img 
                    src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&q=80" 
                    alt="Healthcare Professional" 
                    className="w-full rounded-xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Competitive Advantage */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 text-center">Your Competitive Advantage</h2>
          <div className="bg-black/20 border border-gray-800 rounded-2xl p-8 max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-black/30 p-6 rounded-xl border border-gray-700">
                <Award className="w-12 h-12 text-golden mb-4" />
                <h3 className="text-xl font-bold mb-2">Market Leadership</h3>
                <p className="text-gray-300">
                  Position WeCare Staffing as the go-to healthcare staffing solution in your markets by reaching more potential clients than your competitors.
                </p>
              </div>
              
              <div className="bg-black/30 p-6 rounded-xl border border-gray-700">
                <Building className="w-12 h-12 text-golden mb-4" />
                <h3 className="text-xl font-bold mb-2">Scalable Growth</h3>
                <p className="text-gray-300">
                  Expand into new healthcare markets and specialties with a proven lead generation system that scales with your business goals.
                </p>
              </div>
              
              <div className="bg-black/30 p-6 rounded-xl border border-gray-700">
                <TrendingUp className="w-12 h-12 text-golden mb-4" />
                <h3 className="text-xl font-bold mb-2">Predictable Revenue</h3>
                <p className="text-gray-300">
                  Transform your business development from unpredictable to consistent with a steady flow of qualified healthcare facility leads.
                </p>
              </div>
            </div>
            
            <div className="mt-8">
              <h3 className="text-xl font-bold mb-4 text-golden text-center">Client Success Stories</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-black/30 p-6 rounded-xl border border-gray-700">
                  <p className="text-gray-300 italic mb-4">
                    "After our initial meeting yesterday, I was impressed by the potential of this system. We're starting implementation on Monday to transform our lead generation process and connect with more healthcare facilities efficiently."
                  </p>
                  <p className="text-golden font-semibold">
                    — Philip McParlane, CEO of CRM Inputs, Starting Implementation Monday
                  </p>
                </div>
                
                <div className="bg-black/30 p-6 rounded-xl border border-gray-700">
                  <p className="text-gray-300 italic mb-4">
                    "The quality of leads we're getting is remarkable. These are healthcare facilities actively looking for staffing solutions, and the personalized approach makes all the difference in getting responses from busy administrators."
                  </p>
                  <p className="text-golden font-semibold">
                    — National Healthcare Recruitment Firm, 42 new clients in 90 days
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* When Do We Get Started Section */}
        <div className="mt-16 mb-16">
          <div className="bg-gradient-to-r from-golden/20 to-black/30 rounded-2xl p-12 border border-golden/30 max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-golden">
              WHEN DO WE GET STARTED?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Every day without this system means missed opportunities to connect with healthcare facilities that need your staffing solutions. Your competitors are already reaching out to these facilities - don't let them get there first.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
                <h3 className="text-2xl font-bold text-golden mb-2">40-60</h3>
                <p className="text-gray-300">Qualified Meetings Monthly</p>
              </div>
              <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
                <h3 className="text-2xl font-bold text-golden mb-2">300%</h3>
                <p className="text-gray-300">ROI Within 90 Days</p>
              </div>
              <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
                <h3 className="text-2xl font-bold text-golden mb-2">4-10</h3>
                <p className="text-gray-300">New Clients Monthly</p>
              </div>
            </div>
            <p className="text-lg text-gray-300 mb-8">
              Just like Philip from CRM Inputs, you too can transform your lead generation process and start seeing results within days. The system is ready - the question is, are you?
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}